import React, { useState, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { ChartBarTerpenesProps, Item, TerpeneDataRow } from './types';
import ProductSelector from './selectors/ProductSelector';
import DiseaseCategorySelector from './selectors/DiseaseCategorySelector';
import { transformData, getTooltipCallbacks } from './Chart.Common';

const ChartBarTerpenes: React.FC<ChartBarTerpenesProps> = ({ data }) => {
    const filteredItems = useMemo(() =>
            data.filter((item: Item<TerpeneDataRow>) => item.HEADER?.startsWith("Concentration of terpenes")),
        [data]
    );

    // console.log("ChartBarTerpenes : filteredItems =>" , filteredItems);

    const [visualizationMode, setVisualizationMode] = useState<'direct' | 'percentage' | 'normalized' | 'logarithmic'>('logarithmic');
    const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
    const [compoundFilter, setCompoundFilter] = useState<'all' | 'inCommon' | 'notInCommon'>('all');
    const [showNonNumeric, setShowNonNumeric] = useState<boolean>(true);
    const [selectedDiseaseCategory, setSelectedDiseaseCategory] = useState<string>('All');
    const [diseaseInchiKeys, setDiseaseInchiKeys] = useState<string[]>([]);

    const handleModeChange = (event: SelectChangeEvent<'direct' | 'percentage' | 'normalized' | 'logarithmic'>) => {
        setVisualizationMode(event.target.value as 'direct' | 'percentage' | 'normalized' | 'logarithmic');
    };

    const handleNonNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowNonNumeric(event.target.checked);
    };

    const chartData = useMemo(() => transformData(
        filteredItems,
        selectedTitles,
        visualizationMode,
        (item: Item<TerpeneDataRow>) => `${item.TITLE || 'Unknown'}`,
        (row: TerpeneDataRow) => Number(row.CONCENTRATION) || 0,
        (row: TerpeneDataRow) => row.NAME,
        () => null, // No STDEV in terpene data
        () => '% Dry Weight',
        compoundFilter,
        showNonNumeric,
        diseaseInchiKeys
    ), [filteredItems, selectedTitles, visualizationMode, compoundFilter, showNonNumeric, diseaseInchiKeys]);

    // console.log('Chart Data Terpenes:', chartData);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: `Terpene Concentrations (${visualizationMode.charAt(0).toUpperCase() + visualizationMode.slice(1)})`,
            },
            tooltip: {
                callbacks: getTooltipCallbacks(
                    filteredItems,
                    (item: Item<TerpeneDataRow>) => `${item.TITLE || 'Unknown'}`,
                    (row: TerpeneDataRow) => row.NAME,
                    (row: TerpeneDataRow) => Number(row.CONCENTRATION) || 0,
                    () => null,
                    () => '% Dry Weight',
                    visualizationMode
                ),
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Terpenes',
                },
            },
            y: {
                title: {
                    display: true,
                    text: visualizationMode === 'percentage' ? '% of Batch Total' :
                        visualizationMode === 'normalized' ? 'Normalized Value (%)' :
                            visualizationMode === 'logarithmic' ? 'Log10 Normalized (%)' : '% Dry Weight',
                },
                beginAtZero: true,
            },
        },
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <ProductSelector
                    items={filteredItems}
                    selectedProducts={selectedTitles}
                    setSelectedProducts={setSelectedTitles}
                    compoundFilter={compoundFilter}
                    setCompoundFilter={setCompoundFilter}
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Visualization Mode</InputLabel>
                    <Select
                        value={visualizationMode}
                        onChange={handleModeChange}
                        label="Visualization Mode"
                        size="small"
                    >
                        <MenuItem value="direct">Direct Value</MenuItem>
                        <MenuItem value="percentage">% Range</MenuItem>
                        <MenuItem value="normalized">Unitary Normalization</MenuItem>
                        <MenuItem value="logarithmic">Logarithmic Scale</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={<Checkbox checked={showNonNumeric} onChange={handleNonNumericChange} />}
                    label="Show Non-Numeric"
                    sx={{ m: 0 }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                {filteredItems.length > 0 && chartData.labels.length > 0 ? (
                    <Box sx={{ width: '80%' }}>
                        <Bar data={chartData} options={options} />
                    </Box>
                ) : (
                    <Typography>No valid terpene concentration data available.</Typography>
                )}
            </Box>
        </Box>
    );
};

export default ChartBarTerpenes;
